import type { Ref } from 'vue';
import type { Filter, LogicalFilter, LogicalOperator } from '#layers/data-model/public';
import type { FilterAction, FilterActionOptions } from '#layers/filters/components/Filter/utils/filter-action';
import { cloneDeep } from 'lodash-es';
import { isLogicalFilterAnd } from '#layers/data-model/public';
import { createFilterActionResolver } from '#layers/filters/components/Filter/utils/filter-action';

interface UseLogicalFilterOptions extends FilterActionOptions {
	addGroupToFront?: boolean;
}

export function useLogicalFilter(filter: Ref<LogicalFilter>, options?: UseLogicalFilterOptions) {
	function allows(clause: Filter) {
		return options?.allowsClause?.(clause) ?? true;
	}

	const { resolve, resolveWithValue } = createFilterActionResolver(options ?? {});

	function handleAdd(action: FilterAction | undefined): number | undefined {
		const clause = action?.create();
		if (!clause || !allows(clause)) return undefined;
		const insertedIndex = getFilterList(filter.value).length;
		filter.value = addFilter(filter.value, clause);
		return insertedIndex;
	}

	function handleAddCondition(condition: Filter): number {
		const insertedIndex = getFilterList(filter.value).length;
		filter.value = addFilter(filter.value, condition);
		return insertedIndex;
	}

	function handleRemove(index: number) {
		filter.value = removeFilterAtIndex(filter.value, index);
	}

	function handleUpdate(index: number, update: Filter) {
		filter.value = cloneDeep(updateFilterAtIndex(filter.value, index, update));
	}

	function handleDuplicate(index: number) {
		const newFilter = cloneDeep(getFilterAtIndex(filter.value, index)!);
		filter.value = insertFilter(filter.value, newFilter, index + 1);
	}

	function handleGroupAdd() {
		filter.value = addGroup(filter.value, options?.addGroupToFront);
	}

	function handleTurnIntoGroup(index: number) {
		filter.value = replaceFilterAtIndex(filter.value, index, createGroup([getFilterAtIndex(filter.value, index)!]));
	}

	function toggledOperator(value: LogicalFilter): LogicalFilter {
		return isLogicalFilterAnd(value) ? { _or: value._and } : { _and: value._or };
	}

	function handleToggleOperator() {
		filter.value = toggledOperator(filter.value);
	}

	function handleClearFilter() {
		filter.value = { _and: [] };
	}

	return {
		resolveAction: resolve,
		resolveActionWithValue: resolveWithValue,
		canToggleOperator: () => allows(toggledOperator(filter.value)),
		handleAdd,
		handleAddCondition,
		handleRemove,
		handleUpdate,
		handleDuplicate,
		handleTurnIntoGroup,
		handleGroupAdd,
		handleToggleOperator,
		handleClearFilter,
	};
}

export function getFilterList(filter: LogicalFilter): Filter[] {
	return isLogicalFilterAnd(filter)
		? filter._and
		: filter._or;
}

function getFilterAtIndex(filter: LogicalFilter, index: number) {
	return getFilterList(filter)[index];
}

function filterOp(filter: LogicalFilter): LogicalOperator {
	return isLogicalFilterAnd(filter) ? '_and' : '_or';
}

/** The group a filter UI inserts: a conjunction of the given clauses. */
export function createGroup(clauses: Filter[] = []): Filter {
	return { _and: clauses };
}

function addGroup<TFilter extends LogicalFilter>(filter: TFilter, toFront?: boolean): TFilter {
	const group = createGroup();
	const filters = [...getFilterList(filter)];

	if (toFront === true) {
		filters.unshift(group);
	}
	else {
		filters.push(group);
	}

	return { [filterOp(filter)]: filters } as TFilter;
}

function removeFilterAtIndex<TFilter extends LogicalFilter>(filter: TFilter, index: number): TFilter {
	return {
		[filterOp(filter)]: getFilterList(filter).filter((_, i) => i !== index),
	} as TFilter;
}

function updateFilterAtIndex<TFilter extends LogicalFilter>(filter: TFilter, index: number, newFilter: Filter): TFilter {
	return {
		[filterOp(filter)]: getFilterList(filter).map((f, i) => (i === index ? newFilter : f)),
	} as TFilter;
}

function addFilter<TFilter extends LogicalFilter>(filter: TFilter, newFilter: Filter): TFilter {
	return {
		[filterOp(filter)]: [...getFilterList(filter), newFilter],
	} as TFilter;
}

function insertFilter<TFilter extends LogicalFilter>(filter: TFilter, newFilter: Filter, index: number): TFilter {
	const filters = [...getFilterList(filter)];
	return {
		[filterOp(filter)]: [
			...filters.slice(0, index),
			newFilter,
			...filters.slice(index),
		],
	} as TFilter;
}

function replaceFilterAtIndex<TFilter extends LogicalFilter>(filter: TFilter, index: number, newFilter: Filter): TFilter {
	const filters = [...getFilterList(filter)];
	return {
		[filterOp(filter)]: [
			...filters.slice(0, index),
			newFilter,
			...filters.slice(index + 1),
		],
	} as TFilter;
}
